import {zodResolver} from '@hookform/resolvers/zod';
import {Autocomplete, Button, CircularProgress, Stack, TextField} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';

import {useChangeDataFormStateStore} from 'presentation/pages/private/profile/store/changeDataFormStateStore';
import {useGetBanks} from 'presentation/pages/private/refund/pages/Request/hooks/useGetBanks';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {ConfirmChangeBankModal} from '../../ConfirmChangeBankModal';
import {DiscardChangesModal} from '../../DiscardChangesModal';
import {BankFormProps, ModalsType} from './type';
import {BankDataFormValues, BankDataSchema} from './validation';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';

const {cancel, save, loading, notFound} = translations['pt-br'].commons;
const {bank} = translations['pt-br'].privateFlow.profile.changeData;
const {bank: bankError} = translations['pt-br'].privateFlow.profile.changeData.bankErrors;

const BankForm = ({
	onCancel,
	data,
	onSave,
	isSubmitting,
	bankAccountFieldsInvalid,
}: BankFormProps) => {
	const [isModalsOpen, setIsModalsOpen] = useState<Record<ModalsType, boolean>>({
		confirmChanges: false,
		discardChanges: false,
	});
	const {loadingList, options, open, handleClose, handleOpen} = useGetBanks();
	const setChangeDataFormIsDirtyState = useChangeDataFormStateStore(
		state => state.updateFormState,
	);
	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);

	const methods = useForm<BankDataFormValues>({
		resolver: zodResolver(BankDataSchema),
		mode: 'onChange',

		defaultValues: {
			bank: data?.bank,
			account: data?.account,
			agency: data?.agency,
			accountDigit: data?.accountDigit || '',
			agencyDigit: data?.agencyDigit || '',
		},
	});

	const {
		watch,
		control,
		handleSubmit,
		formState: {errors, isDirty, isValid},
	} = methods;

	const watchedData = watch();

	const handleToggleModalOpen = (modalType: ModalsType) => {
		setIsModalsOpen(prev => ({
			...prev,
			[modalType]: !prev[modalType],
		}));
	};

	const handleBack = () => {
		if (!isNewProfileFeatureFlagEnabled && isDirty) {
			handleToggleModalOpen('discardChanges');
			return;
		}
		onCancel();
	};

	const handleConfirmChanges = async (formValues: BankDataFormValues) => {
		try {
			await onSave(formValues);
		} finally {
			handleToggleModalOpen('confirmChanges');
		}
	};

	useEffect(() => {
		setChangeDataFormIsDirtyState({
			isFormDirty: isDirty,
		});
	}, [isDirty]);

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(handleConfirmChanges)}>
				<Stack spacing={2} mt={2}>
					<Controller
						control={control}
						name="bank"
						render={({field, fieldState}) => {
							const {onChange, value} = field;
							const {error} = fieldState;

							return (
								<Autocomplete
									open={open}
									onOpen={handleOpen}
									onClose={handleClose}
									onChange={(_event, selectedOptions) => {
										onChange(selectedOptions);
									}}
									value={value || null}
									isOptionEqualToValue={(option, valueOption) =>
										option.code === valueOption.code
									}
									getOptionLabel={option =>
										option
											? `${option.code} - ${formatText(option?.description)}`
											: ''
									}
									options={options ?? []}
									loading={loadingList}
									loadingText={loading}
									noOptionsText={notFound}
									renderInput={params => (
										<TextField
											{...params}
											label={bank.bankName}
											error={!!errors.bank}
											placeholder={bank.bankName}
											slotProps={{
												input: {
													...params.InputProps,
													endAdornment: (
														<React.Fragment>
															{loadingList ? (
																<CircularProgress
																	color="inherit"
																	size={20}
																/>
															) : null}
															{params.InputProps.endAdornment}
														</React.Fragment>
													),
												},
											}}
											helperText={error ? bankError : undefined}
										/>
									)}
								/>
							);
						}}
					/>

					<Stack direction="row" spacing={2} flex={1}>
						<Controller
							control={control}
							name="agency"
							render={({field}) => (
								<TextField
									{...field}
									onChange={event => {
										const filteredValue = event.target.value.replace(/\D/g, '');
										field.onChange(filteredValue);
									}}
									error={!!errors.agency || bankAccountFieldsInvalid}
									helperText={
										errors.agency?.message ? errors.agency.message : undefined
									}
									fullWidth
									slotProps={{htmlInput: {maxLength: 5, inputMode: 'numeric'}}}
									label={bank.agency}
									placeholder={bank.agency}
								/>
							)}
						/>
						<Controller
							control={control}
							name="agencyDigit"
							render={({field}) => (
								<TextField
									{...field}
									onChange={event => {
										const filteredValue = event.target.value
											.toLocaleLowerCase()
											.replace(/[^0-9x]/g, '');
										field.onChange(filteredValue);
									}}
									error={!!errors.agencyDigit}
									helperText={
										errors.agencyDigit?.message
											? errors.agencyDigit.message
											: undefined
									}
									fullWidth
									slotProps={{htmlInput: {maxLength: 1}}}
									label={`${bank.digit} ${bank.optional}`}
									placeholder={`${bank.digit} ${bank.optional}`}
								/>
							)}
						/>
					</Stack>
					<Stack direction="row" spacing={2} flex={1}>
						<Controller
							control={control}
							name="account"
							render={({field}) => (
								<TextField
									{...field}
									onChange={event => {
										const filteredValue = event.target.value.replace(/\D/g, '');
										field.onChange(filteredValue);
									}}
									error={!!errors.account || bankAccountFieldsInvalid}
									helperText={
										errors.account?.message ? errors.account.message : undefined
									}
									fullWidth
									slotProps={{htmlInput: {maxLength: 12, inputMode: 'numeric'}}}
									label={bank.account}
									placeholder={bank.account}
								/>
							)}
						/>

						<Controller
							control={control}
							name="accountDigit"
							render={({field}) => (
								<TextField
									{...field}
									onChange={event => {
										const filteredValue = event.target.value
											.toLocaleLowerCase()
											.replace(/[^0-9x]/g, '');
										field.onChange(filteredValue);
									}}
									error={!!errors.accountDigit}
									helperText={
										errors.accountDigit?.message
											? errors.accountDigit.message
											: undefined
									}
									fullWidth
									slotProps={{htmlInput: {maxLength: 2}}}
									label={`${bank.digit} ${bank.optional}`}
									placeholder={`${bank.digit} ${bank.optional}`}
								/>
							)}
						/>
					</Stack>

					<Stack direction="row" flex={1} justifyContent="flex-end" spacing={2}>
						<Button size="large" variant="outlined" onClick={handleBack}>
							{cancel}
						</Button>
						<Button
							disabled={!isValid}
							size="large"
							onClick={() => handleToggleModalOpen('confirmChanges')}>
							{save}
						</Button>
					</Stack>
				</Stack>
				<DiscardChangesModal
					open={isModalsOpen.discardChanges}
					setOpen={() => handleToggleModalOpen('discardChanges')}
					onConfirm={onCancel}
				/>
				{watchedData.bank ? (
					<ConfirmChangeBankModal
						data={{
							name: `${watchedData.bank.code} - ${watchedData.bank.description}`,
							accountWithDigit: watchedData.accountDigit
								? `${watchedData.account}-${watchedData.accountDigit}`
								: watchedData.account,
							agency: watchedData.agencyDigit
								? `${watchedData.agency}-${watchedData.agencyDigit}`
								: watchedData.agency,
						}}
						isSubmitting={isSubmitting}
						open={isModalsOpen.confirmChanges}
						setOpen={() => handleToggleModalOpen('confirmChanges')}
						onConfirm={handleSubmit(handleConfirmChanges)}
					/>
				) : null}
			</form>
		</FormProvider>
	);
};

export {BankForm};
